import {
  ActionIcon,
  Box,
  Button,
  FileButton,
  Flex,
  Group,
  LoadingOverlay,
  Menu,
  Modal,
  NumberInput,
  Select,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useEffect, useRef, useState } from 'react';

import { RangeCalendar } from '@mantine/dates';
import {
  IconBeach,
  IconDotsVertical,
  IconEye,
  IconMoneybag,
  IconReceiptRefund,
  IconTableExport,
  IconTrash,
} from '@tabler/icons';
import { DataTable } from 'mantine-datatable';
import { useNavigate } from 'react-router-dom';
import { PaidBankHour } from '../../@types/bank-hour';
import { BalanceText } from '../../components/balance-text';
import { ConfirmModal } from '../../components/confirm-modal';
import { CustomCalendar } from '../../components/custom-calendar';
import { CustomDatePicker } from '../../components/custom-date-picker';
import { CustomLoader } from '../../components/custom-loader';
import { Page } from '../../components/page';
import { PaymentReceiptModal } from '../../components/payment-receipt-modal';
import { getCompaniesMinRequest } from '../../data/company';
import { getUserFiltersRequest } from '../../data/filters';
import {
  useBulkSettleBankHoursData,
  useCalculateBankHours,
  useCheckBankHoursToUse,
  useGetBankHours,
  usePayBankHour,
  useSettleBankHours,
  useToUseBankHour,
} from '../../data/hooks/bank-hours';
import { useIsMobile } from '../../hooks/useIsMobile';
import { BankHour } from '../../models/bank-hour';
import { CompanyMin } from '../../models/company';
import { UserRole } from '../../models/user';
import { formatLocale } from '../../providers/dayjs-plugins';
import {
  errorNotification,
  successNotification,
} from '../../providers/mantine-notifications';
import { downloadFile, formatBRL, realToUSCash } from '../../utils/helpers';
import { GetFilterUsersResponse } from '../../utils/types/data/services/filters';

type PageModalState =
  | 'pay'
  | 'use'
  | 'settle'
  | 'bulk-settle-confirmation'
  | null;
type ConfirmState = 'pay' | 'use' | null;

const CALENDAR_INITIAL_STATE: [Date | null, Date | null] = [null, null];

export function BankHours() {
  const page = useRef(1);
  const limit = useRef(10);
  const resetRef = useRef<() => void>(null);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [paidReceipt, setPaidReceipt] = useState<{
    payments: PaidBankHour[];
    period: string;
  } | null>(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [companyList, setCompanyList] = useState<CompanyMin[]>([]);
  const [registeredUserList, setRegisteredUserList] = useState<
    GetFilterUsersResponse[]
  >([]);

  const [selectedBankHours, setSelectedBankHours] = useState<BankHour[]>([]);
  const [pageModal, setPageModal] = useState<PageModalState>(null);
  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const [selectedDateToPay, setSelectedDateToPay] = useState<
    [Date | null, Date | null]
  >(CALENDAR_INITIAL_STATE);
  const [selectedDateToUse, setSelectedDateToUse] = useState<Date[]>([]);
  const [, setCsvFile] = useState<File | null>(null);
  const [csvPreviewData, setCsvPreviewData] = useState<any[]>([]);
  const [bulkStartMonth, setBulkStartMonth] = useState<Date | null>(null);
  const [bulkEndMonth, setBulkEndMonth] = useState<Date | null>(null);
  const [isParsingCsv, setIsParsingCsv] = useState(false);

  const {
    loading: getBankHoursLoader,
    fetch: getBankHoursFetcher,
    response: getBankHoursResponse,
  } = useGetBankHours();

  const {
    fetch: calculateBankHoursFetcher,
    loading: calculateBankHoursLoader,
    response: calculateBankHoursResponse,
  } = useCalculateBankHours();

  const { fetch: payBankHourFetcher, loading: payBankHourLoader } =
    usePayBankHour();

  const { fetch: useBankHourFetcher, loading: useBankHourLoader } =
    useToUseBankHour();

  const {
    fetch: checkBankHoursToUseFetcher,
    loading: checkBankHoursToUseLoader,
    response: checkBankHoursToUseResponse,
  } = useCheckBankHoursToUse();

  const { fetch: settleBankHoursFetcher, loading: settleBankHoursLoader } =
    useSettleBankHours();
  const { fetch: bulkSettleDataFetcher, loading: bulkSettleDataLoading } =
    useBulkSettleBankHoursData();

  const formFilter = useForm({
    initialValues: {
      page: page.current,
      limit: limit.current,
      user: '',
    },
  });

  const settleForm = useForm({
    initialValues: {
      userId: '',
      hours: undefined,
      minutes: 0,
      totalValue: '',
      startMonth: null as Date | null,
      endMonth: null as Date | null,
    },
    validate: {
      userId: (v) => (!v ? 'Selecione um funcionário' : null),
      hours: (v) => (v !== undefined && v < 0 ? 'Inválido' : null),
      minutes: (v) =>
        v !== undefined && (v < 0 || v > 59) ? 'Entre 0 e 59' : null,
      totalValue: (v) => (!v?.trim() ? 'Informe o valor' : null),
    },
  });

  async function getBankHours(isReset = false) {
    const validatedValues: any = {};

    Object.entries(formFilter.values).forEach(([key, val]) => {
      if (val !== '' && val !== null && val !== undefined) {
        validatedValues[key] = val;
      }
    });

    if (isReset) {
      await getBankHoursFetcher({
        params: {
          page: 1,
          limit: 10,
        },
      });
    } else {
      await getBankHoursFetcher({
        params: {
          ...validatedValues,
          page: page.current,
          limit: limit.current,
        },
      });
    }
  }

  async function handlePageChange(newPage: number) {
    page.current = newPage;
    getBankHours();
  }

  function handleCloseModal() {
    setConfirm(null);
    setSelectedDateToPay(CALENDAR_INITIAL_STATE);
    setSelectedDateToUse([]);
    setSelectedBankHours([]);
    setPageModal(null);
    settleForm.reset();
    setCsvFile(null);
    setCsvPreviewData([]);
    setBulkStartMonth(null);
    setBulkEndMonth(null);
  }

  async function handleSettleBankHours() {
    const validation = settleForm.validate();
    if (validation.hasErrors) return;

    const values = settleForm.values;
    let period = '';

    if (values.startMonth && values.endMonth) {
      const start = formatLocale(values.startMonth, 'MM/YY');
      const end = formatLocale(values.endMonth, 'MM/YY');
      period = `${start} - ${end}`;
    } else if (values.startMonth) {
      period = formatLocale(values.startMonth, 'MM/YY');
    } else if (values.endMonth) {
      period = formatLocale(values.endMonth, 'MM/YY');
    }

    await settleBankHoursFetcher({
      data: {
        userId: /^\d+$/.test(values.userId) ? Number(values.userId) : undefined,
        username: /^\d+$/.test(values.userId) ? undefined : values.userId,
        hours: values.hours,
        minutes: values.minutes,
        totalValue: realToUSCash(values.totalValue),
        period: period.toUpperCase() || undefined,
      },
      onSuccess: (response: Blob) => {
        downloadFile(
          response,
          `quitacao-banco-horas-${String(new Date(Date.now()).valueOf()).slice(
            7,
            11,
          )}.pdf`,
        );
        successNotification({
          title: 'Recibo gerado com sucesso!',
          message: 'O download do PDF iniciou automaticamente.',
        });
        handleCloseModal();
      },
    });
  }

  async function handleBulkSettle(file: File | null) {
    if (!file) return;
    resetRef.current?.();
    setIsParsingCsv(true);
    setCsvFile(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split(/\r?\n/);
      if (lines.length < 2) {
        setIsParsingCsv(false);
        errorNotification({
          title: 'Arquivo inválido',
          message: 'O arquivo CSV parece estar vazio.',
        });
        return;
      }

      // Tenta encontrar a linha do cabeçalho procurando por palavras-chave
      let headerIndex = -1;
      let nameIndex = -1;
      let bhIndex = -1;
      let totalIndex = -1;

      for (let i = 0; i < Math.min(lines.length, 5); i++) {
        const h = lines[i].split(',').map((item) => item.trim().toUpperCase());
        const nIdx = h.findIndex((item) => item.includes('NOME COMPLETO'));
        const bIdx = h.findIndex((item) => item.includes('BH ATUAL'));
        const tIdx = h.findIndex(
          (item) => item.includes('TOTAL') || item.includes('VALOR'),
        );

        if (nIdx !== -1 && bIdx !== -1 && tIdx !== -1) {
          headerIndex = i;
          nameIndex = nIdx;
          bhIndex = bIdx;
          totalIndex = tIdx;
          break;
        }
      }

      if (headerIndex === -1) {
        setIsParsingCsv(false);
        errorNotification({
          title: 'Cabeçalhos inválidos',
          message:
            'Não foi possível encontrar as colunas necessárias (NOME COMPLETO, BH ATUAL e TOTAL) no arquivo.',
        });
        return;
      }

      const parsedRecords: any[] = [];
      for (let i = headerIndex + 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const columns = line.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/);
        const name = columns[nameIndex]?.replace(/"/g, '').trim();
        const bh = columns[bhIndex]?.replace(/"/g, '').trim();
        const total = columns[totalIndex]?.replace(/"/g, '').trim();

        if (name && bh && total) {
          const timeParts = bh.split(':');
          const hours = Number(timeParts[0]) || 0;
          const minutes = Number(timeParts[1]) || 0;
          const seconds = Number(timeParts[2]) || 0;

          // Considera apenas BH ATUAL positivo
          if (
            (hours > 0 || minutes > 0 || seconds > 0) &&
            !bh.startsWith('-')
          ) {
            parsedRecords.push({ name, bh, total });
          }
        }
      }

      if (parsedRecords.length === 0) {
        setIsParsingCsv(false);
        errorNotification({
          title: 'Nenhum dado encontrado',
          message: 'Não foi possível encontrar registros válidos no CSV.',
        });
        return;
      }

      setCsvPreviewData(parsedRecords);
      setPageModal('bulk-settle-confirmation');
      setIsParsingCsv(false);
    };
    reader.readAsText(file);
  }

  function updateCsvRecord(index: number, field: string, value: string) {
    const newData = [...csvPreviewData];
    newData[index] = { ...newData[index], [field]: value };
    setCsvPreviewData(newData);
  }

  function removeCsvRecord(index: number) {
    const newData = [...csvPreviewData];
    newData.splice(index, 1);
    setCsvPreviewData(newData);
  }

  async function submitBulkSettle() {
    if (!csvPreviewData.length) return;

    let period = '';
    if (bulkStartMonth && bulkEndMonth) {
      const start = formatLocale(bulkStartMonth, 'MM/YY');
      const end = formatLocale(bulkEndMonth, 'MM/YY');
      period = `${start} - ${end}`;
    } else if (bulkStartMonth) {
      period = formatLocale(bulkStartMonth, 'MM/YY');
    } else if (bulkEndMonth) {
      period = formatLocale(bulkEndMonth, 'MM/YY');
    }

    await bulkSettleDataFetcher({
      records: csvPreviewData.map((r) => ({
        username: r.name,
        bhAtual: r.bh,
        totalValue: realToUSCash(r.total),
        period: period.toUpperCase() || undefined,
      })),
      onSuccess: (response: Blob) => {
        downloadFile(response, `quitacao-banco-horas-${Date.now()}.pdf`);
        successNotification({
          title: 'Quitação em massa gerada!',
          message: 'O download do PDF iniciou automaticamente.',
        });
        handleCloseModal();
      },
    });
  }

  async function handlePayBankHour() {
    setConfirm(null);
    const formatedDates = selectedDateToPay
      .map((item) => item?.toISOString())
      .filter((date): date is string => date !== undefined);

    if (!selectedBankHours.length) {
      errorNotification({
        title: 'Selecione ao menos 1 usuário',
        message: 'Tente novamente!',
      });
      return;
    }

    if (!formatedDates[1]) {
      errorNotification({
        title: 'Selecione um intervalo de datas',
        message: 'Tente novamente',
      });
      return;
    }

    const period = `${formatLocale(
      formatedDates[0],
      'DD/MM/YY',
    )} a ${formatLocale(formatedDates[formatedDates.length - 1], 'DD/MM/YY')}`;

    await payBankHourFetcher({
      data: {
        // only who has something to pay: one failing user cancels the batch
        users: (calculateBankHoursResponse ?? []).map((item) => item.user),
        interval: formatedDates,
      },
      onSuccess: (paid) => {
        successNotification({
          title: 'Banco de horas pago com successo!',
          message: 'Tudo certo',
        });
        handleCloseModal();
        getBankHours();
        setPaidReceipt({ payments: paid, period });
      },
    });
  }

  async function handleUseBankHour() {
    setConfirm(null);
    const formatedDates = selectedDateToUse.map((item) => item?.toISOString());

    if (!selectedBankHours.length) {
      errorNotification({
        title: 'Selecione ao menos 1 usuário',
        message: 'Tente novamente!',
      });
      return;
    }

    if (!formatedDates.length) {
      errorNotification({
        title: 'Selecione um intervalo de datas',
        message: 'Tente novamente',
      });
      return;
    }

    await useBankHourFetcher({
      data: {
        users: selectedBankHours.map((item) => item.user),
        interval: formatedDates,
      },
      onSuccess: () => {
        successNotification({
          title: 'Banco de horas concedido com successo!',
          message: 'Tudo certo',
        });
        handleCloseModal();
        getBankHours();
      },
    });
  }

  const usersWithoutHoursToPay = (calculateBankHoursResponse ?? []).filter(
    (item) => item.totalTime <= 0,
  );
  const usersWithoutBalanceToUse = (checkBankHoursToUseResponse ?? []).filter(
    (item) => !item.isAllowedToUse,
  );
  const canPay =
    !!selectedDateToPay[1] &&
    !!calculateBankHoursResponse?.length &&
    usersWithoutHoursToPay.length === 0;
  const canUse =
    selectedDateToUse.length > 0 &&
    !!checkBankHoursToUseResponse?.length &&
    usersWithoutBalanceToUse.length === 0;

  async function getUsers() {
    try {
      setPageLoading(true);
      const response = await getUserFiltersRequest({
        role: UserRole.EMPLOYEE,
      });
      setRegisteredUserList(response);
      setPageLoading(false);
    } catch (error) {
      setPageLoading(false);
      errorNotification({
        title: 'Erro ao buscar usuários.',
        message: 'tente novamente',
      });
    }
  }

  async function getCompanies() {
    try {
      setPageLoading(true);
      const response = await getCompaniesMinRequest();
      setCompanyList(response);
      setPageLoading(false);
    } catch (error) {
      setPageLoading(false);
      errorNotification({
        title: 'Erro ao buscar empresas.',
        message: 'tente novamente',
      });
    }
  }
  useEffect(() => {
    if (selectedDateToPay[1]) {
      const fetchData = async () => {
        await calculateBankHoursFetcher({
          params: {
            users: selectedBankHours.map((bankItem) => bankItem.user).join(','),
            interval: selectedDateToPay
              .map((item) => item?.toISOString())
              .join(','),
          },
        });
      };
      fetchData();
    }

    if (selectedDateToUse.length) {
      const fetchData = async () => {
        await checkBankHoursToUseFetcher({
          params: {
            users: selectedBankHours.map((bankItem) => bankItem.user).join(','),
            interval: selectedDateToUse
              .map((item) => item?.toISOString())
              .join(','),
          },
        });
      };
      fetchData();
    }
  }, [selectedDateToPay, selectedDateToUse.length]);

  useEffect(() => {
    getBankHours();
    getUsers();
    getCompanies();
  }, []);

  return (
    <Page title="Banco de horas">
      <LoadingOverlay visible={isParsingCsv} overlayBlur={2} />
      <CustomLoader loading={pageLoading} />
      <Flex m={16} direction="column">
        <Flex align="center" justify="end" wrap="wrap" gap="xs">
          <Menu shadow="md" width={200}>
            <Menu.Target>
              <Button
                mr={8}
                rightIcon={<IconTableExport size={18} />}
                disabled={!selectedBankHours.length}
              >
                Ações
              </Button>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Label>Tipos de recibo</Menu.Label>
              <Menu.Item
                icon={<IconMoneybag size={14} />}
                onClick={() => setPageModal('pay')}
              >
                Pagar horas
              </Menu.Item>
              <Menu.Item
                icon={<IconBeach size={14} />}
                onClick={() => setPageModal('use')}
              >
                Usar horas
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>

          <Button
            leftIcon={<IconReceiptRefund size={16} />}
            variant="light"
            onClick={() => setPageModal('settle')}
          >
            Quitação
          </Button>

          <FileButton
            resetRef={resetRef}
            onChange={handleBulkSettle}
            accept=".csv"
          >
            {(props) => (
              <Button
                {...props}
                leftIcon={<IconTableExport size={16} />}
                variant="outline"
                ml={8}
                loading={bulkSettleDataLoading}
              >
                Importar quitação (CSV)
              </Button>
            )}
          </FileButton>
        </Flex>
        <Flex wrap="wrap" justify="start" mb={8}>
          <form
            onSubmit={formFilter.onSubmit(() => {
              page.current = 1;
              getBankHours();
            })}
          >
            <Flex align="center">
              <Select
                searchable
                clearable
                label="Funcionário"
                placeholder="selecione o funcionário"
                data={registeredUserList?.map((item) => ({
                  label: item.username,
                  value: String(item.id),
                }))}
                mb={16}
                mr={8}
                {...formFilter.getInputProps('user')}
              />
              <Select
                searchable
                clearable
                label="Empresa"
                placeholder="selecione a empresa"
                data={companyList?.map((item) => ({
                  label: item.fantasyName,
                  value: String(item.id),
                }))}
                mb={16}
                mr={8}
                {...formFilter.getInputProps('company')}
              />
              <Button mb={-10} type="submit">
                Filtrar
              </Button>
            </Flex>
          </form>
        </Flex>
        <DataTable
          selectedRecords={selectedBankHours}
          onSelectedRecordsChange={(bHour) => setSelectedBankHours(bHour)}
          recordsPerPage={10}
          onPageChange={handlePageChange}
          totalRecords={getBankHoursResponse?.meta.totalItems}
          page={page.current}
          fetching={getBankHoursLoader}
          records={getBankHoursResponse?.items}
          noRecordsText="Sem registros"
          withBorder={false}
          borderRadius="sm"
          striped
          highlightOnHover
          height="66vh"
          verticalSpacing={'md'}
          fontSize={'sm'}
          columns={[
            {
              accessor: 'username',
              title: 'Nome',
              width: 300,
            },
            {
              accessor: 'companyName',
              title: 'Empresa',
              width: 300,
            },
            {
              accessor: 'createdAt',
              title: 'Criado em',
              render: ({ createdAt }) => {
                return formatLocale(createdAt, 'DD/MM/YY');
              },
            },
            {
              accessor: 'amount',
              title: 'Saldo',
              render: ({ composedAmount }) => (
                <BalanceText value={composedAmount} />
              ),
            },
            {
              accessor: 'menu',
              title: '',
              render: ({ id }) => (
                <Menu>
                  <Menu.Target>
                    <Button color="blue" variant="subtle" w={40} p={0}>
                      <IconDotsVertical />
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown style={{ position: 'absolute' }}>
                    <Menu.Item
                      onClick={() => navigate(`/bank-hours/${id}/history`)}
                      icon={<IconEye size={14} />}
                    >
                      Ver
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              ),
            },
          ]}
        />
      </Flex>
      <Modal
        fullScreen={isMobile}
        title="Pagar banco de horas"
        opened={pageModal === 'pay'}
        onClose={() => handleCloseModal()}
        closeOnClickOutside={false}
        closeOnEscape={true}
        size={'xl'}
      >
        <Flex gap={'xl'} direction={isMobile ? 'column' : 'row'}>
          <RangeCalendar
            value={selectedDateToPay}
            onChange={setSelectedDateToPay}
            firstDayOfWeek="sunday"
            locale="pt-br"
          />
          <Box>
            {!!selectedDateToPay[1] && usersWithoutHoursToPay.length > 0 && (
              <Text color="red" fw={'bold'} size={'sm'}>
                Sem horas a pagar no período:{' '}
                {usersWithoutHoursToPay.map((u) => u.username).join(', ')}.
                Remova da seleção para continuar.
              </Text>
            )}
            {!!selectedDateToPay[1] &&
              !calculateBankHoursLoader &&
              calculateBankHoursResponse?.length !==
                selectedBankHours.length && (
                <Text color="orange" size={'sm'}>
                  Alguns funcionários selecionados não têm horas no período e
                  ficaram de fora.
                </Text>
              )}
            {!selectedDateToPay[1] && (
              <Text color="dimmed" size={'sm'}>
                Selecione o período que será pago.
              </Text>
            )}
            <DataTable
              withBorder={false}
              borderRadius="sm"
              highlightOnHover
              records={calculateBankHoursResponse ?? []}
              noRecordsIcon
              noRecordsText=""
              fetching={calculateBankHoursLoader}
              columns={[
                {
                  accessor: 'username',
                  title: 'Funcionário',
                  width: '250px',
                },
                {
                  accessor: 'totalTimeFormated',
                  title: 'Tempo disponível',
                  width: '200px',
                  render: ({ totalTime, totalTimeFormated }) => (
                    <BalanceText
                      value={totalTime}
                      formatted={totalTimeFormated}
                    />
                  ),
                },
              ]}
            />
          </Box>
        </Flex>
        <Group position="right">
          <Button
            loading={payBankHourLoader}
            onClick={() => setConfirm('pay')}
            disabled={!canPay}
          >
            Pagar horas
          </Button>
        </Group>
      </Modal>
      <Modal
        fullScreen={isMobile}
        title="Usar banco de horas"
        opened={pageModal === 'use'}
        onClose={() => handleCloseModal()}
        closeOnClickOutside={false}
        closeOnEscape={true}
        size={'xl'}
      >
        <Flex gap={'xl'} direction={isMobile ? 'column' : 'row'}>
          <CustomCalendar
            value={selectedDateToUse}
            onChange={(dates) => setSelectedDateToUse(dates)}
            firstDayOfWeek="sunday"
          />
          <Box>
            {usersWithoutBalanceToUse.length > 0 && (
              <Text color="red" fw={'bold'} size={'sm'}>
                Saldo insuficiente para os dias selecionados:{' '}
                {usersWithoutBalanceToUse.map((u) => u.username).join(', ')}.
                Remova da seleção para continuar.
              </Text>
            )}
            {!selectedDateToUse.length && (
              <Text color="dimmed" size={'sm'}>
                Selecione os dias da folga.
              </Text>
            )}
            <DataTable
              withBorder={false}
              borderRadius="sm"
              highlightOnHover
              records={checkBankHoursToUseResponse ?? []}
              noRecordsIcon
              noRecordsText=""
              fetching={checkBankHoursToUseLoader}
              columns={[
                {
                  accessor: 'username',
                  title: 'Funcionário',
                },
                {
                  accessor: 'totalResquestedTime',
                  title: 'Total requisitado',
                },
                {
                  accessor: 'totalAvailableTime',
                  title: 'Saldo disponível',
                  render: ({ totalAvailableTime }) => (
                    <BalanceText value={totalAvailableTime} />
                  ),
                },
              ]}
            />
          </Box>
        </Flex>
        <Group position="right">
          <Button
            loading={useBankHourLoader}
            onClick={() => setConfirm('use')}
            disabled={!canUse}
          >
            Usar horas
          </Button>
        </Group>
      </Modal>

      <ConfirmModal
        opened={confirm === 'pay'}
        title="Confirmar pagamento"
        confirmLabel="Pagar horas"
        loading={payBankHourLoader}
        onCancel={() => setConfirm(null)}
        onConfirm={handlePayBankHour}
      >
        <Text>
          Pagar as horas de <b>{calculateBankHoursResponse?.length ?? 0}</b>{' '}
          funcionário(s) no período selecionado? As horas pagas saem do saldo e
          o período não poderá ser pago de novo.
        </Text>
      </ConfirmModal>
      <ConfirmModal
        opened={confirm === 'use'}
        title="Confirmar folga"
        confirmLabel="Usar horas"
        loading={useBankHourLoader}
        onCancel={() => setConfirm(null)}
        onConfirm={handleUseBankHour}
      >
        <Text>
          Conceder folga em <b>{selectedDateToUse.length}</b> dia(s) para{' '}
          <b>{selectedBankHours.length}</b> funcionário(s)? Os dias serão
          justificados como banco de horas.
        </Text>
      </ConfirmModal>

      <PaymentReceiptModal
        payments={paidReceipt?.payments ?? null}
        period={paidReceipt?.period ?? ''}
        onClose={() => setPaidReceipt(null)}
      />

      {/* Modal Quitação de Banco de Horas */}
      <Modal
        fullScreen={isMobile}
        title="Quitação de Banco de Horas"
        opened={pageModal === 'settle'}
        onClose={() => handleCloseModal()}
        closeOnClickOutside={false}
        closeOnEscape={true}
        size={'md'}
      >
        <Flex direction="column" gap="sm">
          <Select
            label="Funcionário"
            withAsterisk
            placeholder="Selecione ou digite o nome"
            searchable
            creatable
            getCreateLabel={(query) => `+ Utilizar "${query}"`}
            data={[
              ...registeredUserList.map((u) => ({
                label: u.username,
                value: String(u.id),
              })),
              ...(settleForm.values.userId &&
              !registeredUserList.some(
                (u) => String(u.id) === settleForm.values.userId,
              )
                ? [
                    {
                      label: settleForm.values.userId,
                      value: settleForm.values.userId,
                    },
                  ]
                : []),
            ]}
            {...settleForm.getInputProps('userId')}
          />

          <Flex gap="sm">
            <NumberInput
              label="Horas"
              placeholder="0"
              min={0}
              required
              style={{ flex: 1 }}
              {...settleForm.getInputProps('hours')}
            />
            <NumberInput
              label="Minutos"
              placeholder="0"
              min={0}
              max={59}
              required
              style={{ flex: 1 }}
              {...settleForm.getInputProps('minutes')}
            />
          </Flex>

          <Flex gap="sm">
            <CustomDatePicker
              label="Período inicial"
              placeholder="Início"
              clearable
              style={{ flex: 1 }}
              {...settleForm.getInputProps('startMonth')}
            />
            <CustomDatePicker
              label="Mês final"
              placeholder="Fim"
              clearable
              style={{ flex: 1 }}
              {...settleForm.getInputProps('endMonth')}
            />
          </Flex>

          <TextInput
            label="Valor total (R$)"
            placeholder="Ex: 350.00"
            required
            value={settleForm.values.totalValue}
            onChange={(e) =>
              settleForm.setFieldValue('totalValue', formatBRL(e.target.value))
            }
          />

          <Text size="xs" color="dimmed" mt={4}>
            Ao confirmar o recibo de quitação será gerado automaticamente em
            PDF.
          </Text>
        </Flex>

        <Group position="right" mt="md">
          <Button variant="default" onClick={() => handleCloseModal()}>
            Cancelar
          </Button>
          <Button
            loading={settleBankHoursLoader}
            leftIcon={<IconReceiptRefund size={16} />}
            onClick={() => handleSettleBankHours()}
          >
            Gerar Recibo
          </Button>
        </Group>
      </Modal>

      <Modal
        fullScreen={isMobile}
        title="Quitação de Banco de Horas"
        opened={pageModal === 'bulk-settle-confirmation'}
        onClose={() => handleCloseModal()}
        size="60%"
      >
        <Text size="sm" mb="md">
          Confira os dados abaixo extraídos do arquivo CSV. Somente registros
          com saldo positivo serão processados pelo sistema.
        </Text>

        <Flex gap="sm" mb="md">
          <CustomDatePicker
            label="Período inicial"
            placeholder="Início"
            clearable
            style={{ flex: 1 }}
            value={bulkStartMonth}
            onChange={setBulkStartMonth}
          />
          <CustomDatePicker
            label="Mês final"
            placeholder="Fim"
            clearable
            style={{ flex: 1 }}
            value={bulkEndMonth}
            onChange={setBulkEndMonth}
          />
        </Flex>

        <DataTable
          withBorder
          borderRadius="sm"
          highlightOnHover
          records={csvPreviewData}
          height={600}
          columns={[
            {
              accessor: 'name',
              title: 'Nome Completo',
              width: 400,
              render: (record, index) => (
                <TextInput
                  value={record.name}
                  onChange={(e) =>
                    updateCsvRecord(index, 'name', e.target.value)
                  }
                />
              ),
            },
            {
              accessor: 'bh',
              title: 'BH Atual',
              render: (record, index) => (
                <TextInput
                  value={record.bh}
                  onChange={(e) => updateCsvRecord(index, 'bh', e.target.value)}
                />
              ),
            },
            {
              accessor: 'total',
              title: 'Total',
              render: (record, index) => (
                <TextInput
                  value={record.total}
                  onChange={(e) =>
                    updateCsvRecord(index, 'total', formatBRL(e.target.value))
                  }
                />
              ),
            },
            {
              accessor: 'actions',
              title: 'Ações',
              textAlignment: 'right',
              render: (_, index) => (
                <ActionIcon color="red" onClick={() => removeCsvRecord(index)}>
                  <IconTrash size={16} />
                </ActionIcon>
              ),
            },
          ]}
        />

        <Group position="right" mt="xl">
          <Button variant="default" onClick={() => handleCloseModal()}>
            Cancelar
          </Button>
          <Button
            loading={bulkSettleDataLoading}
            leftIcon={<IconReceiptRefund size={16} />}
            onClick={() => submitBulkSettle()}
          >
            Confirmar e Gerar Recibos
          </Button>
        </Group>
      </Modal>
    </Page>
  );
}
